// @ts-nocheck
import { useJobRepository } from '../repositories';
import { useCompatQuery, useCompatMutation } from './useApolloCompat';
import {
  JOBS_QUERY,
  JOB_COST_ENTRIES_QUERY,
  CREATE_JOB_COST_ENTRY_MUTATION,
  JOB_BUDGET_SUMMARY_QUERY,
  JOB_COST_REPORT_QUERY,
  EXPORT_JOB_COST_REPORT_MUTATION,
} from '../graphql/operations';
import type { Job, JobCostEntry, JobBudgetSummary } from '../types/jobCosting';

// ── Response Types ──────────────────────────────────────────────────────

export interface JobCostReportRow {
  jobId: string;
  jobName: string;
  jobCode: string;
  budgetAmount: number;
  totalLabor: number;
  totalMaterial: number;
  totalEquipment: number;
  totalOverhead: number;
  totalOther: number;
  totalCost: number;
  variance: number;
}

// ── Basic CRUD Hooks (via Repository) ───────────────────────────────────

export function useJobs(variables?: {
  status?: string;
  page?: number;
  perPage?: number;
}) {
  const repo = useJobRepository();
  return repo.useList(variables);
}

export function useJob(id: string | undefined) {
  const repo = useJobRepository();
  return repo.useGet(id ?? null);
}

export function useCreateJob() {
  const repo = useJobRepository();
  return repo.useCreate();
}

export function useUpdateJob() {
  const repo = useJobRepository();
  return repo.useUpdate();
}

export function useDeleteJob() {
  const repo = useJobRepository();
  return repo.useDelete();
}

// ── Specialized Hooks (via useApolloCompat) ─────────────────────────────

export function useJobCostEntries(
  jobId: string | undefined,
  variables?: { page?: number; perPage?: number },
) {
  return useCompatQuery(JOB_COST_ENTRIES_QUERY, {
    variables: { jobId, ...variables },
    skip: !jobId,
  });
}

export function useCreateJobCostEntry() {
  return useCompatMutation(CREATE_JOB_COST_ENTRY_MUTATION, {
    refetchQueries: [
      { query: JOB_COST_ENTRIES_QUERY },
      { query: JOB_BUDGET_SUMMARY_QUERY },
      { query: JOBS_QUERY },
    ],
  });
}

export function useJobBudgetSummary(jobId: string | undefined) {
  return useCompatQuery(JOB_BUDGET_SUMMARY_QUERY, {
    variables: { jobId },
    skip: !jobId,
  });
}

export function useJobCostReport(variables: {
  startDate: string;
  endDate: string;
  jobIds?: string[];
  departmentId?: string;
}) {
  return useCompatQuery(JOB_COST_REPORT_QUERY, {
    variables,
    skip: !variables.startDate || !variables.endDate,
  });
}

export function useExportJobCostReport() {
  return useCompatMutation(EXPORT_JOB_COST_REPORT_MUTATION);
}
